import React from "react";

/* What the next message is attached to.
 *
 * Sits directly on top of the composer, not in the header. Context belongs next
 * to the thing it scopes — a chip in the header describes the page, a chip
 * touching the input describes the message you are about to send. That is the
 * difference between "which project am I looking at" and "which project is this
 * question about", and only the second one matters at the moment you type.
 *
 * Built as a chip stack rather than a single project line, because this is where
 * everything you attach will pile up: files, sections, requirements, a
 * questionnaire. Adding those later means pushing another item into `items`, not
 * redesigning the bar.
 */

const KIND_ICON = {
  project: (
    <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2.5h8a2 2 0 0 1 2 2V17a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </svg>
  ),
  file: (
    <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <polyline points="14 3 14 8 19 8" />
    </svg>
  ),
  section: (
    <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="7" x2="19" y2="7" />
      <line x1="5" y1="12" x2="15" y2="12" />
      <line x1="5" y1="17" x2="17" y2="17" />
    </svg>
  ),
};

const CopilotContextBar = ({ items = [], onRemove, onOpen }) => {
  if (!items.length) return null;

  return (
    <div className="mx-auto mb-1.5 w-full max-w-[860px]">
      {/* No "CONTEXT" label. Shouting a category name at someone who can see a
          folder icon and a project name is a label explaining a label. */}
      <div className="flex flex-wrap items-center gap-1.5">

        {items.map((item) => {
          /* A project chip opens that project beside the conversation. It was
             the one thing on screen naming a project you could not click, which
             makes it look like a label rather than the handle it is. The × keeps
             its own hit area, so removing and opening never collide. */
          const openable = item.kind === "project" && !!onOpen;
          return (
          <span
            key={`${item.kind}:${item.id}`}
            title={item.sublabel ? `${item.label} — ${item.sublabel}` : item.label}
            className="inline-flex max-w-[300px] items-center gap-1.5 rounded-full border border-[#A04A97]/25 bg-white py-[3px] pl-1 pr-1 text-[12.5px] font-medium text-[#8B3F83] shadow-[0_1px_2px_rgba(24,16,28,0.04)] animate-copilot-rise motion-reduce:animate-none"
          >
            {openable ? (
              <button
                type="button"
                onClick={() => onOpen(item)}
                title={`Open ${item.label}`}
                className="flex min-w-0 items-center gap-1.5 rounded-full px-1 py-[1px] transition-colors duration-150 hover:bg-[#A04A97]/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97]"
              >
                <span className="flex-none text-[#A04A97]/70">{KIND_ICON[item.kind]}</span>
                <span className="truncate">{item.label}</span>
                {item.sublabel && (
                  <span className="flex-none text-[11.5px] font-normal text-[#77707E]">
                    {item.sublabel}
                  </span>
                )}
              </button>
            ) : (
              <span className="flex min-w-0 items-center gap-1.5 px-1">
                <span className="flex-none text-[#A04A97]/70">{KIND_ICON[item.kind] || KIND_ICON.file}</span>
                <span className="truncate">{item.label}</span>
                {item.sublabel && (
                  <span className="flex-none text-[11.5px] font-normal text-[#77707E]">
                    {item.sublabel}
                  </span>
                )}
              </span>
            )}
            <button
              type="button"
              onClick={() => onRemove?.(item)}
              aria-label={`Remove ${item.label}`}
              className="flex h-4 w-4 flex-none items-center justify-center rounded text-[#A04A97]/55 transition-colors duration-150 hover:bg-[#A04A97]/15 hover:text-[#A04A97] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97]"
            >
              <svg aria-hidden="true" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </span>
          );
        })}
      </div>
    </div>
  );
};

export default CopilotContextBar;
